import type {
  IdvPsc,
  IdvPscStatus,
  IdvSessionStatus,
} from '@pflegenavi/shared/api';

export interface IdvPscAction {
  psc: IdvPsc;
  action: 'open' | 'retry';
}

export interface IdvActions {
  primary: 'start' | 'continue' | null;
  /** The existing session is valid but superseded by the offer to start a new one. */
  collapseExisting: boolean;
  /** MangoPay validated the session but our verified flag still waits for its webhook. */
  awaitingConfirmation: boolean;
  pscActions: IdvPscAction[];
}

const NEGATIVE_STATUSES: IdvSessionStatus[] = [
  'REFUSED',
  'EXPIRED',
  'OUT_OF_DATE',
];
const FINISHED_PSC_STATUSES: IdvPscStatus[] = ['VALIDATED', 'ABANDONED'];

const pscActions = (pscs: IdvPsc[]): IdvPscAction[] =>
  pscs.flatMap((psc): IdvPscAction[] => {
    if (psc.status === 'PENDING_VALIDATION' && psc.hosted_url) {
      return [{ psc, action: 'open' }];
    }
    if (psc.status === 'REJECTED') {
      return [{ psc, action: 'retry' }];
    }
    return [];
  });

/** What the KYC dialog offers; a validated session allows a new one only once no PSC is open. */
export const getIdvActions = (
  status: IdvSessionStatus | null | undefined,
  pscs: IdvPsc[],
  kycVerified: boolean
): IdvActions => {
  if (!status || NEGATIVE_STATUSES.includes(status)) {
    return {
      primary: 'start',
      collapseExisting: false,
      awaitingConfirmation: false,
      pscActions: [],
    };
  }
  if (status === 'PENDING' || status === 'REVIEW') {
    // MangoPay reviews a REVIEW session manually; its hosted URL has nothing left to do
    return {
      primary: status === 'PENDING' ? 'continue' : null,
      collapseExisting: false,
      awaitingConfirmation: false,
      pscActions: pscActions(pscs),
    };
  }
  const allPscsFinished = pscs.every((psc) =>
    FINISHED_PSC_STATUSES.includes(psc.status)
  );
  if (!allPscsFinished) {
    return {
      primary: null,
      collapseExisting: false,
      awaitingConfirmation: false,
      pscActions: pscActions(pscs),
    };
  }
  // validated by MangoPay but not yet KYC'd on our side: the KYC webhook is still due
  return kycVerified
    ? {
        primary: 'start',
        collapseExisting: true,
        awaitingConfirmation: false,
        pscActions: [],
      }
    : {
        primary: null,
        collapseExisting: false,
        awaitingConfirmation: true,
        pscActions: [],
      };
};

const IDV_SESSION_LANGUAGES: Record<string, string> = {
  de: 'de-DE',
  en: 'en-US',
};

/** MangoPay hosted IDV locale for an app language, e.g. `'de'` -> `'de-DE'`; undefined lets MangoPay pick. */
export const idvSessionLanguage = (
  language: string | undefined
): string | undefined =>
  language ? IDV_SESSION_LANGUAGES[language.split('-')[0]] : undefined;
